import { useSeason } from '../../context/SeasonContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './TeamModal.module.css';
import closeIcon from '../../assets/icons/closeIconWhite.svg';
import avatarPlaceholder from '../../assets/icons/avatar.svg';
import deleteIcon from '../../assets/icons/closeRed.svg';
import sfu from '../../assets/icons/sfu.svg';
import plus from '../../assets/icons/plus.svg';
import {
  addTeamMember,
  assignTeamCaptain,
  deleteTeam,
  getTeamOverview,
  leaveTeam,
  removeTeamMember,
  updateTeamMemberCustomRole
} from '../../api/teams';
import { getPublicShowcaseCase } from '../../api/caseShowcase';
import {
  approveTeamVacancyApplication,
  createTeamVacancy,
  declineTeamVacancyApplication,
  deleteTeamVacancy,
  getTeamVacancies,
  updateTeamVacancy
} from '../../api/teamVacancies';
import { searchUsers } from '../../api/search';
import { ModalPortal } from '../ModalPortal/ModalPortal';
import { useAuth } from '../../context/AuthContext';
import { isAdminLikeRole } from '../../utils/roles';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return String(value);
  }
}

function TeamModal({ team, isOpen, onClose, onTeamChanged }) {
  const [activeTab, setActiveTab] = useState('members');
  const [dragOffset, setDragOffset] = useState(0);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [showcaseCase, setShowcaseCase] = useState(null);
  const [showcaseLoading, setShowcaseLoading] = useState(false);
  const [showcaseError, setShowcaseError] = useState('');
  const [vacancies, setVacancies] = useState([]);
  const [vacanciesLoading, setVacanciesLoading] = useState(false);
  const [vacanciesError, setVacanciesError] = useState('');
  const [vacancyForm, setVacancyForm] = useState({ title: '', description: '' });
  const [vacancyEditId, setVacancyEditId] = useState(null);
  const [vacancyEditForm, setVacancyEditForm] = useState({ title: '', description: '' });
  const [vacancyActionError, setVacancyActionError] = useState('');

  const [memberQuery, setMemberQuery] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateList, setCandidateList] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [roleDrafts, setRoleDrafts] = useState({});

  const modalContentRef = useRef(null);
  const dragStartRef = useRef({ y: 0, isDragging: false });
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useAuth();

  const teamId = team?.id;
  const normalizedRole = String(user?.role || '').toUpperCase();
  const { canEdit } = useSeason();
  const canManageMembers = canEdit && ((team?.systemRole || '').toUpperCase() === 'CAPTAIN'
    || normalizedRole === 'MENTOR'
    || isAdminLikeRole(normalizedRole));
    
  const isCurrentCaptain = (team?.systemRole || '').toUpperCase() === 'CAPTAIN';

  const loadOverview = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getTeamOverview(teamId);
      setOverview(data);
      const nextDrafts = {};
      data.members.forEach((member) => {
        nextDrafts[member.user_id] = member.custom_role || '';
      });
      setRoleDrafts(nextDrafts);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить данные команды.');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  const loadVacancies = useCallback(async () => {
    if (!teamId) return;
    setVacanciesLoading(true);
    setVacanciesError('');
    try {
      const data = await getTeamVacancies(teamId);
      setVacancies(Array.isArray(data) ? data : []);
    } catch (err) {
      setVacanciesError(err.message || 'Не удалось загрузить вакансии команды.');
    } finally {
      setVacanciesLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('members');
    setActionError('');
    setMemberQuery('');
    setCustomRole('');
    setSelectedCandidate(null);
    setCandidateList([]);
    setVacancies([]);
    setVacanciesError('');
    setVacancyActionError('');
    setVacancyForm({ title: '', description: '' });
    setVacancyEditId(null);
    loadOverview();
    loadVacancies();
  }, [isOpen, loadOverview, loadVacancies]);

  useEffect(() => {
    const caseId = overview?.team?.showcase_case_id || team?.showcase_case_id;
    if (!isOpen || !caseId) {
      setShowcaseCase(null);
      setShowcaseError('');
      return;
    }

    let isActive = true;
    setShowcaseLoading(true);
    setShowcaseError('');
    getPublicShowcaseCase(caseId)
      .then((data) => {
        if (!isActive) return;
        setShowcaseCase(data);
      })
      .catch((err) => {
        if (!isActive) return;
        setShowcaseError(err.message || 'Не удалось загрузить кейс из витрины.');
        setShowcaseCase(null);
      })
      .finally(() => {
        if (isActive) setShowcaseLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isOpen, overview, team]);

  useEffect(() => {
    if (!isOpen || !canManageMembers) return;
    const query = memberQuery.trim();
    if (query.length < 2) {
      setCandidateList([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await searchUsers(query, 10, 0);
        setCandidateList(response.items || []);
      } catch {
        setCandidateList([]);
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [isOpen, canManageMembers, memberQuery]);

  const displayedTeamName = overview?.team?.name || team?.teamName || 'Команда';
  const displayedTeamBalance = overview?.team?.balance ?? team?.balance ?? 0;
  const members = useMemo(() => overview?.members ?? [], [overview]);
  const currentCase = overview?.current_case || null;
  const linkedShowcaseCaseTitle = overview?.team?.showcase_case_title || team?.showcase_case_title || '';
  const trackerChatUrl = overview?.team?.tracker_chat_url || team?.tracker_chat_url || '';

  const memberIds = useMemo(() => new Set(members.map((item) => item.user_id)), [members]);

  const handleDragStart = useCallback((e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = { y: clientY, isDragging: true };
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!dragStartRef.current.isDragging) return;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = clientY - dragStartRef.current.y;
    if (deltaY > 0) {
      e.preventDefault();
      setDragOffset(deltaY);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!dragStartRef.current.isDragging) return;
    if (dragOffset > 100) onClose();
    setDragOffset(0);
    dragStartRef.current = { y: 0, isDragging: false };
  }, [dragOffset, onClose]);

  const handleDragCancel = useCallback(() => {
    setDragOffset(0);
    dragStartRef.current = { y: 0, isDragging: false };
  }, []);

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!teamId || isSubmittingAction) return;

    const query = memberQuery.trim();
    const parsedFromInput = Number(query);
    const candidateId = selectedCandidate?.user_id
      || (Number.isInteger(parsedFromInput) && parsedFromInput > 0 ? parsedFromInput : null);

    if (!candidateId) {
      setActionError('Выбери участника из поиска или введи корректный ID.');
      return;
    }
    if (memberIds.has(candidateId)) {
      setActionError('Этот участник уже в команде.');
      return;
    }

    setActionError('');
    setIsSubmittingAction(true);
    try {
      await addTeamMember(teamId, candidateId, customRole.trim() || null);
      setMemberQuery('');
      setCustomRole('');
      setSelectedCandidate(null);
      setCandidateList([]);
      await loadOverview();
      onTeamChanged?.();
    } catch (err) {
      setActionError(err.message || 'Не удалось добавить участника.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!teamId || isSubmittingAction) return;
    setActionError('');
    setIsSubmittingAction(true);
    try {
      await removeTeamMember(teamId, userId);
      await loadOverview();
      onTeamChanged?.();
    } catch (err) {
      setActionError(err.message || 'Не удалось удалить участника.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleAssignCaptain = async (userId) => {
    if (!teamId || isSubmittingAction) return;
    setActionError('');
    setIsSubmittingAction(true);
    try {
      await assignTeamCaptain(teamId, userId);
      await loadOverview();
      onTeamChanged?.();
    } catch (err) {
      const status = err?.response?.status || err?.status;
      let msg = err.message || 'Не удалось назначить капитана.';
      if (status === 400) msg = 'Нельзя назначить капитаном самого себя.';
      else if (status === 403) msg = 'Только текущий капитан может выполнить это действие.';
      else if (status === 404) msg = 'Пользователь не состоит в команде.';
      setActionError(msg);
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleSaveCustomRole = async (userId) => {
    if (!teamId || isSubmittingAction) return;
    setActionError('');
    setIsSubmittingAction(true);
    try {
      await updateTeamMemberCustomRole(teamId, userId, roleDrafts[userId]?.trim() || null);
      await loadOverview();
    } catch (err) {
      setActionError(err.message || 'Не удалось обновить роль участника.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleLeaveTeam = async () => {
    if (!teamId || isSubmittingAction) return;
    setActionError('');
    setIsSubmittingAction(true);
    try {
      await leaveTeam(teamId);
      onTeamChanged?.();
      onClose();
    } catch (err) {
      setActionError(err.message || 'Не удалось покинуть команду.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamId || isSubmittingAction) return;
    const confirmed = window.confirm('Вы уверены, что хотите удалить команду? Это действие нельзя отменить.');
    if (!confirmed) return;
    setActionError('');
    setIsSubmittingAction(true);
    try {
      await deleteTeam(teamId);
      onTeamChanged?.();
      onClose();
    } catch (err) {
      setActionError(err.message || 'Не удалось удалить команду.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleCreateVacancy = async (e) => {
    e.preventDefault();
    if (!teamId || isSubmittingAction) return;
    const title = vacancyForm.title.trim();
    const description = vacancyForm.description.trim();
    if (!title || !description) {
      setVacancyActionError('Заполни название и описание вакансии.');
      return;
    }
    setVacancyActionError('');
    setIsSubmittingAction(true);
    try {
      await createTeamVacancy(teamId, { title, description });
      setVacancyForm({ title: '', description: '' });
      await loadVacancies();
    } catch (err) {
      setVacancyActionError(err.message || 'Не удалось создать вакансию.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleEditVacancy = (vacancy) => {
    setVacancyEditId(vacancy.vacancy_id);
    setVacancyEditForm({
      title: vacancy.title || '',
      description: vacancy.description || ''
    });
    setVacancyActionError('');
  };

  const handleCancelVacancyEdit = () => {
    setVacancyEditId(null);
    setVacancyActionError('');
  };

  const handleUpdateVacancy = async (vacancyId) => {
    if (!teamId || isSubmittingAction) return;
    const title = vacancyEditForm.title.trim();
    const description = vacancyEditForm.description.trim();
    if (!title || !description) {
      setVacancyActionError('Заполни название и описание вакансии.');
      return;
    }
    setVacancyActionError('');
    setIsSubmittingAction(true);
    try {
      await updateTeamVacancy(teamId, vacancyId, { title, description });
      setVacancyEditId(null);
      await loadVacancies();
    } catch (err) {
      setVacancyActionError(err.message || 'Не удалось обновить вакансию.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleDeleteVacancy = async (vacancyId) => {
    if (!teamId || isSubmittingAction) return;
    const confirmed = window.confirm('Удалить вакансию?');
    if (!confirmed) return;
    setVacancyActionError('');
    setIsSubmittingAction(true);
    try {
      await deleteTeamVacancy(teamId, vacancyId);
      if (vacancyEditId === vacancyId) {
        setVacancyEditId(null);
      }
      await loadVacancies();
    } catch (err) {
      setVacancyActionError(err.message || 'Не удалось удалить вакансию.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleApplicantDecision = async (vacancyId, userId, decision) => {
    if (!teamId || isSubmittingAction) return;
    setVacancyActionError('');
    setIsSubmittingAction(true);
    try {
      if (decision === 'approve') {
        await approveTeamVacancyApplication(teamId, vacancyId, userId);
      } else {
        await declineTeamVacancyApplication(teamId, vacancyId, userId);
      }
      await loadVacancies();
    } catch (err) {
      setVacancyActionError(err.message || 'Не удалось обработать отклик.');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'members', label: 'Состав' },
    { id: 'current', label: 'Текущее проектное задание' },
    { id: 'talents', label: 'Поиск ТАЛАНТОВ' }
  ];

  const renderTabContent = () => {
    if (loading) {
      return <p className={styles.noData}>Загружаем...</p>;
    }

    if (error) {
      return <p className={styles.noData}>{error}</p>;
    }

    switch (activeTab) {
      case 'members':
        return (
          <div className={styles.membersList}>
            {members.map((member) => {
              const displayName = `${member.last_name} ${member.first_name}`.trim() || member.login;
              return (
                <div key={member.user_id} className={styles.memberItem}>
                  <img
                    src={member.avatar_url || avatarPlaceholder}
                    alt={displayName}
                    className={styles.memberAvatar}
                    onError={(e) => {
                      e.currentTarget.src = avatarPlaceholder;
                    }}
                  />
                  <div className={styles.memberMeta}>
                    <button
                      type="button"
                      className={styles.memberProfileLink}
                      onClick={() => navigate(`/search/users/${member.user_id}?q=${encodeURIComponent(member.login || displayName)}`)}
                    >
                      {displayName}
                    </button>
                    <span className={styles.memberLogin}>@{member.login}</span>
                    <span className={styles.memberLogin}>
                      {member.system_role}
                      {member.custom_role ? ` • ${member.custom_role}` : ''}
                    </span>
                  </div>

                  {canManageMembers ? (
                    <div className={styles.memberActions}>
                      <input
                        className={styles.memberRoleInput}
                        type="text"
                        value={roleDrafts[member.user_id] ?? ''}
                        onChange={(e) =>
                          setRoleDrafts((prev) => ({
                            ...prev,
                            [member.user_id]: e.target.value,
                          }))
                        }
                        placeholder="Кастомная роль"
                        maxLength={100}
                      />
                      <button
                        className={styles.roleSaveBtn}
                        type="button"
                        onClick={() => handleSaveCustomRole(member.user_id)}
                        disabled={isSubmittingAction}
                      >
                        Сохранить
                      </button>
                      {member.system_role?.toUpperCase() !== 'CAPTAIN' ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {isCurrentCaptain ? (
                            <button
                              className={styles.roleSaveBtn}
                              type="button"
                              onClick={() => handleAssignCaptain(member.user_id)}
                              disabled={isSubmittingAction}
                            >
                              Назначить капитаном
                            </button>
                          ) : null}
                          <button
                            className={styles.removeMemberBtn}
                            onClick={() => handleRemoveMember(member.user_id)}
                            type="button"
                            disabled={isSubmittingAction}
                            title="Удалить из команды"
                          >
                            <img src={deleteIcon} alt="Удалить" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}

            {canManageMembers ? (
              <form className={styles.addMemberForm} onSubmit={handleAddMember}>
                <p className={styles.formLegend}>Добавить участника</p>
                <input
                  className={styles.addMemberInput}
                  type="text"
                  value={memberQuery}
                  onChange={(e) => {
                    setMemberQuery(e.target.value);
                    setSelectedCandidate(null);
                    setActionError('');
                  }}
                  placeholder="Поиск участника или ID"
                />
                <input
                  className={styles.addMemberInput}
                  type="text"
                  value={customRole}
                  onChange={(e) => setCustomRole(e.target.value)}
                  placeholder="Кастомная роль (необязательно)"
                  maxLength={100}
                />
                <p className={styles.formHint}>Кастомная роль в этом блоке задается для нового участника при добавлении.</p>
                <button className={styles.addMemberBtn} type="submit" disabled={isSubmittingAction}>
                  <img src={plus} alt="+" />
                  Добавить
                </button>
                {searchLoading ? <p className={styles.memberLogin}>Ищем...</p> : null}
                {!searchLoading && candidateList.length > 0 ? (
                  <div className={styles.searchResults}>
                    {candidateList.map((candidate) => (
                      <button
                        key={candidate.user_id}
                        className={styles.searchItem}
                        type="button"
                        onClick={() => {
                          setSelectedCandidate(candidate);
                          setMemberQuery(`${candidate.last_name} ${candidate.first_name}`.trim());
                        }}
                      >
                        {candidate.last_name} {candidate.first_name} (@{candidate.login})
                      </button>
                    ))}
                  </div>
                ) : null}
              </form>
            ) : null}

            <div className={styles.teamActionRow}>
              <button
                className={styles.secondaryActionBtn}
                type="button"
                onClick={handleLeaveTeam}
                disabled={isSubmittingAction}
              >
                Покинуть команду
              </button>
              {canManageMembers ? (
                <button
                  className={styles.dangerActionBtn}
                  type="button"
                  onClick={handleDeleteTeam}
                  disabled={isSubmittingAction}
                >
                  Удалить команду
                </button>
              ) : null}
            </div>
          </div>
        );

      case 'current':
        return (
          <div className={styles.currentCase}>
            <div className={styles.showcaseCard}>
              <div className={styles.showcaseHeader}>
                <p className={styles.showcaseLabel}>Привязанный кейс</p>
                {showcaseLoading ? <span className={styles.showcaseHint}>Загрузка...</span> : null}
              </div>
              {showcaseError ? <p className={styles.showcaseError}>{showcaseError}</p> : null}
              {showcaseCase ? (
                <div className={styles.showcaseBody}>
                  <p className={styles.showcaseTitle}>{showcaseCase.title}</p>
                  <p className={styles.showcaseSubtitle}>{showcaseCase.partner_name}</p>
                  {showcaseCase.track ? <p className={styles.showcaseMeta}>Трек: {showcaseCase.track}</p> : null}
                  {showcaseCase.location ? <p className={styles.showcaseMeta}>Локация: {showcaseCase.location}</p> : null}
                  <div className={styles.showcaseRow}>
                    <span className={styles.showcaseRowLabel}>Задача</span>
                    <span className={styles.showcaseRowValue}>{showcaseCase.problem}</span>
                  </div>
                  {showcaseCase.expected_solution ? (
                    <div className={styles.showcaseRow}>
                      <span className={styles.showcaseRowLabel}>Ожидаемое решение</span>
                      <span className={styles.showcaseRowValue}>{showcaseCase.expected_solution}</span>
                    </div>
                  ) : null}
                  {showcaseCase.contacts ? (
                    <div className={styles.showcaseRow}>
                      <span className={styles.showcaseRowLabel}>Контакты</span>
                      <span className={styles.showcaseRowValue}>{showcaseCase.contacts}</span>
                    </div>
                  ) : null}
                  {showcaseCase.rewards ? (
                    <div className={styles.showcaseRow}>
                      <span className={styles.showcaseRowLabel}>Награды</span>
                      <span className={styles.showcaseRowValue}>{showcaseCase.rewards}</span>
                    </div>
                  ) : null}
                  {showcaseCase.attachments ? (
                    <div className={styles.showcaseRow}>
                      <span className={styles.showcaseRowLabel}>Материалы</span>
                      <a className={styles.showcaseLink} href={showcaseCase.attachments} target="_blank" rel="noreferrer">
                        Открыть материалы
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : (
                !showcaseLoading && !showcaseError ? (
                  <p className={styles.showcaseEmpty}>Кейс из витрины пока не привязан.</p>
                ) : null
              )}
            </div>
            {currentCase ? (
              <div className={styles.caseCard}>
                <h4 className={styles.caseTitle}>{currentCase.title}</h4>
                <p className={styles.caseDescription}>{currentCase.description || 'Без описания.'}</p>
                <div className={styles.caseInfo}>
                  <span className={styles.caseInfoItem}>
                    <span className={styles.caseInfoLabel}>Создан:</span> {formatDate(currentCase.created_at)}
                  </span>
                  <span className={styles.caseInfoItem}>
                    <span className={styles.caseInfoLabel}>Документов:</span> {currentCase.documents?.length || 0}
                  </span>
                </div>
              </div>
            ) : (
              <p className={styles.noData}>Текущее проектное задание пока не назначено.</p>
            )}
          </div>
        );

      case 'past':
      case 'talents':
        return (
          <div className={styles.vacancies}>
            {canManageMembers ? (
              <form className={styles.vacancyForm} onSubmit={handleCreateVacancy}>
                <div className={styles.vacancyField}>
                  <label className={styles.vacancyLabel}>Название</label>
                  <input
                    className={styles.vacancyInput}
                    type="text"
                    value={vacancyForm.title}
                    onChange={(e) => setVacancyForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div className={styles.vacancyField}>
                  <label className={styles.vacancyLabel}>Описание</label>
                  <textarea
                    className={styles.vacancyTextarea}
                    value={vacancyForm.description}
                    onChange={(e) => setVacancyForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                <button className={styles.primaryActionBtn} type="submit" disabled={isSubmittingAction}>
                  Опубликовать
                </button>
                {vacancyActionError ? <p className={styles.vacancyError}>{vacancyActionError}</p> : null}
              </form>
            ) : null}

            {vacanciesLoading ? <p className={styles.noData}>Загружаем вакансии...</p> : null}
            {vacanciesError ? <p className={styles.noData}>{vacanciesError}</p> : null}

            {!vacanciesLoading && !vacancies.length ? (
              <p className={styles.noData}>Вакансии пока не опубликованы.</p>
            ) : null}

            <div className={styles.vacancyList}>
              {vacancies.map((vacancy) => {
                const isEditing = vacancyEditId === vacancy.vacancy_id;
                return (
                  <div key={vacancy.vacancy_id} className={styles.vacancyCard}>
                    <div className={styles.vacancyHeader}>
                      {isEditing ? (
                        <input
                          className={styles.vacancyInput}
                          type="text"
                          value={vacancyEditForm.title}
                          onChange={(e) => setVacancyEditForm((prev) => ({ ...prev, title: e.target.value }))}
                        />
                      ) : (
                        <h4 className={styles.vacancyTitle}>{vacancy.title}</h4>
                      )}
                      <span className={styles.vacancyStatus}>
                        {vacancy.is_open ? 'Открыта' : 'Закрыта'}
                      </span>
                    </div>
                    {isEditing ? (
                      <textarea
                        className={styles.vacancyTextarea}
                        value={vacancyEditForm.description}
                        onChange={(e) => setVacancyEditForm((prev) => ({ ...prev, description: e.target.value }))}
                      />
                    ) : (
                      <p className={styles.vacancyDescription}>{vacancy.description}</p>
                    )}

                    <div className={styles.vacancyActions}>
                      {canManageMembers ? (
                        isEditing ? (
                          <>
                            <button
                              className={styles.primaryActionBtn}
                              type="button"
                              onClick={() => handleUpdateVacancy(vacancy.vacancy_id)}
                              disabled={isSubmittingAction}
                            >
                              Сохранить
                            </button>
                            <button className={styles.secondaryActionBtn} type="button" onClick={handleCancelVacancyEdit}>
                              Отмена
                            </button>
                          </>
                        ) : (
                          <>
                            <button className={styles.secondaryActionBtn} type="button" onClick={() => handleEditVacancy(vacancy)}>
                              Редактировать
                            </button>
                            <button
                              className={styles.dangerActionBtn}
                              type="button"
                              onClick={() => handleDeleteVacancy(vacancy.vacancy_id)}
                              disabled={isSubmittingAction}
                            >
                              Удалить
                            </button>
                          </>
                        )
                      ) : null}
                    </div>

                    <div className={styles.applicantsSection}>
                      <p className={styles.applicantsTitle}>
                        Откликнулись: {vacancy.applicants_count}
                      </p>
                      {!vacancy.applicants.length ? (
                        <p className={styles.applicantsEmpty}>Пока нет откликов.</p>
                      ) : (
                        <div className={styles.applicantsList}>
                          {vacancy.applicants.map((applicant) => (
                            <div key={applicant.application_id} className={styles.applicantCard}>
                              <button
                                type="button"
                                className={styles.applicantLink}
                                onClick={() => navigate(`/search/users/${applicant.user_id}?q=${encodeURIComponent(applicant.login)}`)}
                              >
                                {`${applicant.last_name} ${applicant.first_name}`.trim() || applicant.login}
                              </button>
                              <span className={styles.applicantStatus}>{applicant.status}</span>
                              {canManageMembers && applicant.status === 'PENDING' ? (
                                <div className={styles.applicantActions}>
                                  <button
                                    className={styles.primaryActionBtn}
                                    type="button"
                                    onClick={() => handleApplicantDecision(vacancy.vacancy_id, applicant.user_id, 'approve')}
                                    disabled={isSubmittingAction}
                                  >
                                    Да
                                  </button>
                                  <button
                                    className={styles.secondaryActionBtn}
                                    type="button"
                                    onClick={() => handleApplicantDecision(vacancy.vacancy_id, applicant.user_id, 'decline')}
                                    disabled={isSubmittingAction}
                                  >
                                    Нет
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <ModalPortal onClose={onClose} isMobile={isMobile}>
      <div
        className={styles.modalContentWrapper}
        ref={modalContentRef}
        onClick={(e) => e.stopPropagation()}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragCancel}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        style={{
          transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : 'none',
          transition: dragOffset > 0 ? 'none' : 'transform 0.2s'
        }}
      >
        <div className={styles.modalScrollArea}>
          <div className={styles.modalHeader}>
            <div
              className={styles.dragHandleArea}
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
            />
            <div
              className={styles.dragHandle}
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
            />
            <div className={styles.modalHeaderContent}>
              <h2 style={{ marginBlockEnd: '0', marginBlockStart: '0', marginBottom: '10px' }} className={styles.modalTitle}>
                {displayedTeamName}
              </h2>
              <p>
                Баланс: <img src={sfu} alt="sfu" /> {displayedTeamBalance}
              </p>
              {linkedShowcaseCaseTitle ? (
                <p className={styles.teamContextText}>
                  Кейс витрины: <strong>{linkedShowcaseCaseTitle}</strong>
                </p>
              ) : null}
              {trackerChatUrl ? (
                <p className={styles.teamContextText}>
                  <a
                    className={styles.teamContextLink}
                    href={trackerChatUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Открыть чат команды в Яндекс Трекере
                  </a>
                </p>
              ) : null}
            </div>

            <button className={styles.modalClose} onClick={onClose} type="button">
              <img src={closeIcon} alt="Закрыть" />
            </button>
          </div>

          <div className={styles.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => setActiveTab(tab.id)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>

          {actionError ? <p className={styles.modalError}>{actionError}</p> : null}
          <div className={styles.tabContent}>{renderTabContent()}</div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default TeamModal;
